import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  CircularProgress, 
  Snackbar, 
  Alert, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  GridLegacy as Grid,
  Divider,
  Badge,
  Fade,
  Skeleton
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import api from '../api/api';
import SignaturePad from '../components/SignaturePad';
import { useAuth } from '../context/AuthContext';

interface Staff {
  id: number;
  full_name: string;
  role: string;
  email: string;
}

const STAFF_ROLES = [
  'teacher', 'principal', 'assistant_teacher', 'head_teacher', 'counselor', 'librarian', 'accountant', 'secretary'
];

const getRoleColor = (role: string) => {
  const colors: Record<string, string> = {
    teacher: '#1976d2',
    principal: '#d32f2f',
    assistant_teacher: '#388e3c',
    head_teacher: '#f57c00',
    counselor: '#7b1fa2',
    librarian: '#303f9f',
    accountant: '#c2185b',
    secretary: '#5d4037'
  };
  return colors[role] || '#757575';
};

const getRoleIcon = (role: string) => {
  return <SchoolIcon sx={{ fontSize: 16 }} />;
};

const SignatureManagement: React.FC = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [signatures, setSignatures] = useState<Record<number, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});

  useEffect(() => {
    setLoading(true);
    api.get('/users/', { params: { role: STAFF_ROLES, school_id: user?.school_id } })
      .then(res => {
        setStaff(res.data as any[]);
        // Fetch signatures for each staff
        Promise.all((res.data as any[]).map((s: Staff) =>
          api.get('/signature/get-signature', { 
            params: { user_id: s.id, role: s.role }, 
            responseType: 'blob' 
          })
            .then(r => {
              // Create object URL from blob
              return URL.createObjectURL(r.data as Blob);
            })
            .catch((error) => {
              console.log(`No signature found for user ${s.id} (${s.role}):`, error.response?.status);
              return null;
            })
        )).then(urls => {
          const sigMap: Record<number, string | null> = {};
          (res.data as any[]).forEach((s: Staff, i: number) => { 
            sigMap[s.id] = urls[i]; 
          });
          setSignatures(sigMap);
          setLoading(false); // Set loading to false after signatures are processed
        })
        .catch((error) => {
          console.error('Failed to load signatures:', error);
          setLoading(false); // Set loading to false even if signature loading fails
        });
      })
      .catch((error) => {
        console.error('Failed to load staff:', error);
        setSnackbar({open: true, message: 'Failed to load staff', severity: 'error'});
        setLoading(false); // Set loading to false if staff loading fails
      });
  }, [user?.school_id]);

  const handleOpenDialog = (staff: Staff) => {
    setSelectedStaff(staff);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStaff(null);
  };

  const handleSignatureSave = async (dataUrl: string) => {
    if (!selectedStaff) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', String(selectedStaff.id));
      formData.append('role', selectedStaff.role);
      formData.append('image_data', dataUrl);
      await api.post('/signature/upload-signature', formData);
      setSnackbar({open: true, message: 'Signature uploaded successfully', severity: 'success'});
      // Refresh signature
      api.get('/signature/get-signature', { 
        params: { user_id: selectedStaff.id, role: selectedStaff.role }, 
        responseType: 'blob' 
      })
        .then(r => {
          setSignatures(s => ({ ...s, [selectedStaff.id]: URL.createObjectURL(r.data as Blob) }));
        })
        .catch((error) => {
          console.error('Failed to refresh signature:', error);
        });
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to upload signature:', error);
      setSnackbar({open: true, message: 'Failed to upload signature', severity: 'error'});
    } finally {
      setUploading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatRole = (role: string) => {
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Skeleton variant="text" width={300} height={40} sx={{ mx: 'auto', mb: 1 }} />
          <Skeleton variant="text" width={500} height={24} sx={{ mx: 'auto' }} />
        </Box>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item}>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={20} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            fontWeight: 700, 
            color: '#1a237e',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <EditIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          Signature Management
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#666', 
            maxWidth: 600, 
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Manage digital signatures for school staff. Admins can view and upload signatures for all staff members.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: 'white', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar sx={{ 
                bgcolor: '#1976d2', 
                width: 56, 
                height: 56, 
                mx: 'auto', 
                mb: 2 
              }}>
                <PersonIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
                {staff.length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Total Staff Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: 'white', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar sx={{ 
                bgcolor: '#388e3c', 
                width: 56, 
                height: 56, 
                mx: 'auto', 
                mb: 2 
              }}>
                <CheckCircleIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c', mb: 1 }}>
                {Object.values(signatures).filter(sig => sig !== null).length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Signatures Uploaded
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: 'white', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar sx={{ 
                bgcolor: '#f57c00', 
                width: 56, 
                height: 56, 
                mx: 'auto', 
                mb: 2 
              }}>
                <CancelIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', mb: 1 }}>
                {Object.values(signatures).filter(sig => sig === null).length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Pending Signatures
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: 'white', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar sx={{ 
                bgcolor: '#7b1fa2', 
                width: 56, 
                height: 56, 
                mx: 'auto', 
                mb: 2 
              }}>
                <SchoolIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2', mb: 1 }}>
                {new Set(staff.map(s => s.role)).size}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Different Roles
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Staff Table */}
      <Card sx={{ 
        bgcolor: 'white', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        borderRadius: 3,
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fafafa'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Staff Signatures
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
            Click on a staff member to manage their signature
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Staff Member</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Signature Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map((s, index) => (
                <Fade in={true} timeout={300 + index * 100} key={s.id}>
                  <TableRow 
                    sx={{ 
                      '&:hover': { 
                        bgcolor: '#f8f9fa',
                        transform: 'scale(1.01)',
                        transition: 'all 0.2s'
                      },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleOpenDialog(s)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: getRoleColor(s.role),
                            width: 40,
                            height: 40,
                            fontSize: '0.9rem',
                            fontWeight: 600
                          }}
                        >
                          {getInitials(s.full_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a237e' }}>
                            {s.full_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                            ID: {s.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(s.role)}
                        label={formatRole(s.role)}
                        size="small"
                        sx={{
                          bgcolor: `${getRoleColor(s.role)}15`,
                          color: getRoleColor(s.role),
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: getRoleColor(s.role) }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        {s.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {signatures[s.id] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: '#388e3c', fontWeight: 600 }}>
                            Uploaded
                          </Typography>
                          <Tooltip title="View Signature">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(signatures[s.id] as string, '_blank');
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CancelIcon sx={{ color: '#f57c00', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600 }}>
                            Not Uploaded
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(user?.role === 'admin' || user?.role === 'superuser' || user?.id === s.id) && (
                        <Tooltip title={signatures[s.id] ? 'Update Signature' : 'Upload Signature'}>
                          <Button
                            variant={signatures[s.id] ? "outlined" : "contained"}
                            size="small"
                            startIcon={signatures[s.id] ? <EditIcon /> : <UploadIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(s);
                            }}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 2,
                              ...(signatures[s.id] ? {
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                  borderColor: '#1565c0',
                                  bgcolor: '#1976d215'
                                }
                              } : {
                                bgcolor: '#1976d2',
                                '&:hover': {
                                  bgcolor: '#1565c0'
                                }
                              })
                            }}
                          >
                            {signatures[s.id] ? 'Update' : 'Upload'}
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                </Fade>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Signature Upload Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#f8f9fa', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: getRoleColor(selectedStaff?.role || ''), width: 40, height: 40 }}>
            {selectedStaff && getInitials(selectedStaff.full_name)}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
              {signatures[selectedStaff?.id || 0] ? 'Update' : 'Upload'} Signature
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              for {selectedStaff?.full_name} ({formatRole(selectedStaff?.role || '')})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <SignaturePad onSave={handleSignatureSave} />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={uploading}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          {uploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Uploading...
              </Typography>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({...s, open: false}))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(s => ({...s, open: false}))}
          sx={{ 
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiAlert-icon': { fontSize: 24 }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignatureManagement; 